// src/components/common/BranchDropdownMenu.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ToastMe from '@/components/ui/ToastMe';
import {
  fetchBranches as apiFetchBranches,
  deleteBranch as apiDeleteBranch,
} from '@/services/BranchService';
import { setBranchId } from '@/security/Axios';
import { jwtDecode } from 'jwt-decode';

/* ───────────────── Small helpers ───────────────── */

const collator =
  typeof Intl !== 'undefined' ? new Intl.Collator(undefined, { sensitivity: 'base' }) : null;

function computeDisplayName(b = {}) {
  const parts = [b.name, b.title, b.city, b.code, b.address, b._id]
    .map((x) => (x == null ? '' : String(x).trim()))
    .filter(Boolean);

  if (!parts[0] && b.city && b.code) return `${String(b.city).trim()} (${String(b.code).trim()})`;
  return parts[0] || 'Unnamed branch';
}

function sortBranches(list = []) {
  return [...list].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    const an = computeDisplayName(a);
    const bn = computeDisplayName(b);
    return collator ? collator.compare(an, bn) : an.localeCompare(bn);
  });
}

/** Read super-admin from Redux with a JWT fallback */
function useIsSuperAdmin() {
  const fromStore = useSelector((s) => s.Auth?.data?.data?.isSuperAdmin);
  let fromJwt = false;
  try {
    const tok = localStorage.getItem('jwt_token');
    if (tok) {
      const d = jwtDecode(tok);
      fromJwt = !!d?.isSuperAdmin;
    }
  } catch (err) {}
  return !!(fromStore ?? fromJwt);
}

const StatusBadge = ({ status = 1 }) => {
  const map = {
    1: { text: 'Active', wrap: 'bg-emerald-50 text-emerald-700' },
    2: { text: 'Inactive', wrap: 'bg-amber-50 text-amber-700' },
    3: { text: 'Archived', wrap: 'bg-slate-100 text-slate-600' },
  };
  const s = map[status] || map[1];
  return <span className={`text-[11px] px-1.5 py-0.5 rounded ${s.wrap} shrink-0`}>{s.text}</span>;
};

const DefaultPill = () => (
  <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 shrink-0">Default</span>
);

/* ───────────────── Component ───────────────── */

const BranchDropdownMenu = ({ className = '', showAllOption = true }) => {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const listboxRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState([]);
  const [query, setQuery] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);

  const isSuperAdmin = useIsSuperAdmin();

  // read minimal slices to avoid noisy re-renders
  const branchList_fromBranchSlice = useSelector((s) => s.Branch?.list);
  const branchList_fromAuth = useSelector((s) => s.Auth?.data?.data?.branches);
  const branchList_fromGeneral = useSelector((s) => s.GeneralSetting?.branches);

  const activeId_fromBranchSlice = useSelector((s) => s.Branch?.active?._id);
  const activeId_fromAuth = useSelector((s) => s.Auth?.data?.data?.branch_id);
  const activeId_fromGeneral = useSelector((s) => s.GeneralSetting?.active_branch_id);

  const canViewAllBranches = useSelector((s) =>
    s.Auth?.data?.data?.permissions?.includes?.('view_all_branches')
  );
  const companyId_fromAuth = useSelector((s) => s.Auth?.data?.data?.company_id);

  /* LocalStorage fallbacks */
  const lsBranchList = useMemo(
    () => JSON.parse(localStorage.getItem('branch_list') || '[]'),
    []
  );
  const lsActiveId = localStorage.getItem('branch_id');
  const lsActiveName = localStorage.getItem('branch_name');

  /* combine store values */
  const storeBranches =
    branchList_fromBranchSlice || branchList_fromAuth || branchList_fromGeneral || [];

  const storeActiveId =
    activeId_fromBranchSlice || activeId_fromAuth || activeId_fromGeneral || lsActiveId || null;

  /* normalize & sort list (API > store > LS) */
  const branches = useMemo(() => {
    const baseSrc =
      (Array.isArray(fetched) && fetched.length
        ? fetched
        : Array.isArray(storeBranches) && storeBranches.length
        ? storeBranches
        : Array.isArray(lsBranchList)
        ? lsBranchList
        : []);

    const base = baseSrc.map((b) => {
      const displayName = computeDisplayName(b);
      return {
        _id: b._id || b.id,
        name: displayName,
        code: b.code || '',
        city: b.city || '',
        phone: b.phone || '',
        email: b.email || '',
        address: b.address || '',
        status: typeof b.status === 'number' ? b.status : 1,
        isDefault: !!(b.is_default || b.isDefault),
        company_id: b.company_id,
      };
    });

    return sortBranches(base);
  }, [fetched, storeBranches, lsBranchList]);

  /* active branch object */
  const activeBranch = useMemo(() => {
    if (lsActiveId === 'all') return { _id: 'all', name: 'All branches' };
    if (branches?.length) {
      const found = branches.find((b) => String(b._id) === String(storeActiveId));
      if (found) return found;
    }
    if (lsActiveId && lsActiveName) return { _id: lsActiveId, name: lsActiveName };
    return branches[0] || { _id: 'default', name: 'Select a branch' };
  }, [branches, storeActiveId, lsActiveId, lsActiveName]);

  /* ── Always fetch. SuperAdmin adds ?all=1, others get tenant-scoped list ── */
  const loadBranches = async (fromClick = false) => {
    try {
      setLoading(true);
      const list = await apiFetchBranches(isSuperAdmin ? { all: true } : undefined);
      setFetched(Array.isArray(list) ? list : []);
      try {
        dispatch({ type: 'BRANCH/SET_LIST', payload: Array.isArray(list) ? list : [] });
      } catch (err) {}
    } catch (e) {
      console.error(e);
      ToastMe(e?.message || 'Failed to load branches', 'error');
      if (fromClick && !isSuperAdmin) {
        ToastMe('Showing branches for your company scope.', 'blank');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, companyId_fromAuth]);

  /* persist compact snapshot to LS */
  useEffect(() => {
    if (!branches?.length) return;
    const compact = branches.map((b) => ({
      _id: b._id,
      name: b.name,
      code: b.code,
      city: b.city,
      phone: b.phone,
      email: b.email,
      status: b.status,
      is_default: b.isDefault,
      address: b.address,
    }));
    localStorage.setItem('branch_list', JSON.stringify(compact));

    const activeExists = branches.some((b) => String(b._id) === String(storeActiveId));
    if (!activeExists && lsActiveId !== 'all') {
      const preferred = branches.find((b) => b.isDefault) || branches[0];
      if (preferred) {
        localStorage.setItem('branch_id', String(preferred._id));
        localStorage.setItem('branch_name', preferred.name || '');
      }
    }
  }, [branches, storeActiveId, lsActiveId]);

  /* outside click / Esc */
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  /* helpers */
  const prettyName = (b) =>
    b._id === 'all' ? 'All branches' : `${b.name}${b.isDefault ? ' (Main)' : ''}`;

  const selectBranch = (b) => {
    const id = String(b._id);
    const label = computeDisplayName(b);
    localStorage.setItem('branch_id', id);
    localStorage.setItem('branch_name', label);
    setBranchId(id, label);
    try {
      dispatch({ type: 'BRANCH/SET_ACTIVE', payload: { ...b, name: label } });
    } catch (err) {}
    setOpen(false);
    setTimeout(() => window.location.reload(), 150);
  };

  const selectAllBranches = () => {
    localStorage.setItem('branch_id', 'all');
    localStorage.setItem('branch_name', 'All branches');
    setBranchId('all', 'All branches');
    try {
      dispatch({ type: 'BRANCH/SET_ACTIVE', payload: { _id: 'all', name: 'All branches' } });
    } catch (err) {}
    setOpen(false);
    setTimeout(() => window.location.reload(), 150);
  };

  const onDeleteBranch = async (b) => {
    const id = String(b._id);
    if (b.isDefault) {
      ToastMe('You cannot delete the default branch.', 'error');
      return;
    }
    if (!window.confirm(`Delete branch "${computeDisplayName(b)}"? This cannot be undone.`)) return;
    try {
      setDeletingId(id);
      await apiDeleteBranch(id);
      ToastMe('Branch deleted', 'success');
      setFetched((prev) => prev.filter((x) => String(x._id || x.id) !== id));
      const current = JSON.parse(localStorage.getItem('branch_list') || '[]').filter(
        (x) => String(x._id) !== id
      );
      localStorage.setItem('branch_list', JSON.stringify(current));
      if (String(activeBranch?._id) === id) {
        localStorage.removeItem('branch_id');
        localStorage.removeItem('branch_name');
        setTimeout(() => window.location.reload(), 150);
      } else {
        try {
          dispatch({ type: 'BRANCH/REMOVE_ONE', payload: id });
        } catch (err) {}
      }
    } catch (e) {
      console.error(e);
      ToastMe(e?.message || 'Delete failed', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  /* filtering */
  const filtered = useMemo(() => {
    if (!query.trim()) return branches;
    const q = query.trim().toLowerCase();
    return branches.filter((b) =>
      [b.name, b.code, b.city, b.phone, b.email, b.address].some((x) =>
        String(x || '').toLowerCase().includes(q)
      )
    );
  }, [branches, query]);

  const canShowAll = showAllOption && (isSuperAdmin || canViewAllBranches);

  /* keyboard nav */
  const itemsRenderedCount = (canShowAll ? 1 : 0) + (filtered?.length || 0);
  const clampFocus = (n) => Math.max(0, Math.min(itemsRenderedCount - 1, n));
  useEffect(() => {
    if (!open) return;
    const startIdx = (() => {
      if (canShowAll && activeBranch?._id === 'all') return 0;
      const idx = filtered.findIndex((b) => String(b._id) === String(activeBranch?._id));
      return idx >= 0 ? idx + (canShowAll ? 1 : 0) : 0;
    })();
    setFocusIdx(clampFocus(startIdx));
    if (listboxRef.current && typeof listboxRef.current.focus === 'function') {
      listboxRef.current.focus();
    }
  }, [open, filtered, canShowAll, activeBranch?._id]);

  const onKeyDown = (e) => {
    if (!open) return;
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'].includes(e.key)) e.preventDefault();
    if (e.key === 'ArrowDown') setFocusIdx((i) => clampFocus(i + 1));
    if (e.key === 'ArrowUp') setFocusIdx((i) => clampFocus(i - 1));
    if (e.key === 'Home') setFocusIdx(0);
    if (e.key === 'End') setFocusIdx(itemsRenderedCount - 1);
    if (e.key === 'Enter') {
      if (canShowAll && focusIdx === 0) return selectAllBranches();
      const idx = focusIdx - (canShowAll ? 1 : 0);
      const b = filtered[idx];
      if (b) selectBranch(b);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Toggle */}
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="group inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-[280px] justify-between"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-3">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-sm">
            <i className="fa-solid fa-code-branch text-[13px]" />
          </span>
          <span className="flex flex-col items-start leading-4">
            <span className="text-[11px] text-slate-500">Branch</span>
            <span className="text-[13px] font-semibold text-slate-800 line-clamp-1">
              {prettyName(activeBranch)}
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          {loading && <i className="fa-solid fa-spinner fa-spin text-[12px] text-slate-400" />}
          <i
            className={`fa-solid fa-chevron-down text-[12px] text-slate-500 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>

      {/* Popover */}
      {open && (
        <div
          role="listbox"
          tabIndex={0}
          ref={listboxRef}
          onKeyDown={onKeyDown}
          className="absolute z-40 mt-2 w-[420px] rounded-2xl border border-slate-200 bg-white shadow-2xl left-0 focus:outline-none"
          aria-activedescendant={`branch-opt-${focusIdx}`}
        >
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-3 pt-3 pb-2 border-b border-slate-100 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-slate-500">
                {canShowAll ? 'All branches available' : 'My company branches'}
                {branches?.length ? (
                  <span className="ml-1 text-slate-400">· {branches.length}</span>
                ) : null}
              </span>
              <button
                type="button"
                onClick={() => loadBranches(true)}
                className="p-1.5 rounded-md hover:bg-slate-50 text-slate-500"
                title="Refresh"
                aria-label="Refresh branches"
              >
                <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'} text-[12px]`} />
              </button>
            </div>

            <div className="mt-2 relative">
              <i className="fa-solid fa-magnifying-glass absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, code, city, phone, email…"
                className="w-full text-[12px] pl-6 pr-2 py-2 border rounded-lg border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label="Search branches"
              />
            </div>
          </div>

          {/* Body */}
          <ul className="max-h-80 overflow-auto py-2 divide-y divide-slate-100">
            {/* All branches option */}
            {(() => {
              if (!canShowAll) return null;
              const idx = 0;
              const selected = activeBranch?._id === 'all';
              const focused = focusIdx === idx;
              return (
                <li id={`branch-opt-${idx}`} className={`px-2 ${focused ? 'bg-slate-50' : ''}`}>
                  <button
                    type="button"
                    onClick={selectAllBranches}
                    className={`w-full px-2 py-2 text-left rounded-lg hover:bg-slate-50 focus:bg-slate-50 ${
                      selected ? 'bg-slate-100 font-semibold' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        readOnly
                        checked={selected}
                        className="h-[14px] w-[14px] accent-blue-600 mt-[2px] shrink-0"
                        aria-label="All branches"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[14px] text-slate-800 line-clamp-1">All branches</span>
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
                            Aggregate
                          </span>
                        </div>
                        <div className="text-[12px] text-slate-500 mt-0.5">
                          View totals across {branches?.length || 0} branches
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })()}

            {/* Skeleton */}
            {loading && !filtered.length && (
              <>
                {[...Array(3)].map((_, i) => (
                  <li key={`s-${i}`} className="px-4 py-3 animate-pulse">
                    <div className="h-3 w-40 bg-slate-100 rounded mb-2" />
                    <div className="h-2 w-56 bg-slate-100 rounded" />
                  </li>
                ))}
              </>
            )}

            {/* Results */}
            {!loading && filtered?.length ? (
              filtered.map((b, i) => {
                const idx = i + (canShowAll ? 1 : 0);
                const selected = String(activeBranch?._id) === String(b._id);
                const focused = focusIdx === idx;
                return (
                  <li id={`branch-opt-${idx}`} key={b._id} className={`px-2 ${focused ? 'bg-slate-50' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => selectBranch(b)}
                        className={`flex-1 px-2 py-2 text-left rounded-lg hover:bg-slate-50 focus:bg-slate-50 ${
                          selected ? 'bg-slate-100 font-semibold' : ''
                        }`}
                        role="option"
                        aria-selected={selected}
                        title={b.address || ''}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            readOnly
                            checked={selected}
                            className="h-[14px] w-[14px] accent-blue-600 mt-[2px] shrink-0"
                            aria-label={`Select ${b.name}`}
                          />
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[14px] text-slate-800 line-clamp-1">{b.name}</span>
                              {b.isDefault && <DefaultPill />}
                              <StatusBadge status={b.status} />
                            </div>
                            <div className="text-[12px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                              {b.code && (
                                <span>
                                  Code: <span className="font-mono">{b.code}</span>
                                </span>
                              )}
                              {b.city && <span>City: {b.city}</span>}
                              {b.phone && <span>Phone: {b.phone}</span>}
                              {b.email && (
                                <span className="truncate max-w-[160px]">Email: {b.email}</span>
                              )}
                            </div>
                            {b.address && (
                              <div className="text-[12px] text-slate-400 mt-0.5 line-clamp-1">{b.address}</div>
                            )}
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        title={b.isDefault ? 'Default branch cannot be deleted' : 'Delete branch'}
                        disabled={deletingId === String(b._id) || b.isDefault}
                        onClick={() => onDeleteBranch(b)}
                        className={`p-2 rounded-md transition ml-2 shrink-0 ${
                          b.isDefault
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-slate-400 hover:text-red-600 hover:bg-red-50'
                        }`}
                        aria-label={`Delete ${b.name}`}
                      >
                        {deletingId === String(b._id) ? (
                          <i className="fa-solid fa-spinner fa-spin text-[14px]" />
                        ) : (
                          <i className="fa-solid fa-trash text-[14px]" />
                        )}
                      </button>
                    </div>
                  </li>
                );
              })
            ) : (
              !loading && (
                <li className="px-4 py-6 text-sm text-slate-500 text-center">
                  {query.trim() ? (
                    <>
                      No results for <span className="font-medium">“{query}”</span>
                    </>
                  ) : (
                    'No branches found'
                  )}
                </li>
              )
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BranchDropdownMenu;
